// src/pages/Dashboard.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance";
import PromotionFooterWidget from "../components/Promotions/PromotionFooterWidget";
import TermsGate from "../components/TermsGate";

import { Alert, Button, Segmented, Skeleton, Table } from "antd";
import "chart.js/auto";
import { Line as LineChart, Doughnut } from "react-chartjs-2";
import {
  CardTitle,
  PageHeader,
  SectionCard,
  StatStrip,
  StatusTag,
  TabBar,
  fmtDate,
  fmtDateTime,
  tablePagination,
  useFlatPage,
} from "../components/ui";
import { IconRefresh } from "../components/ui/icons";

/* ───────── helpers ───────── */
const cssVar = (name, fallback) => {
  try {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
  } catch {
    return fallback;
  }
};

const FONT = "'Segoe UI', system-ui, -apple-system, Roboto, 'Helvetica Neue', Arial, sans-serif";

const toTitle = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
const getFirstName = (a) => {
  if (!a) return "there";
  if (a.firstName) return toTitle(String(a.firstName).trim());
  const candidate = a.displayName || a.fullName || a.userName || a.email || "";
  if (!candidate) return "there";
  const raw = candidate.replace(/@.*$/, "").replace(/[._-]+/g, " ").trim();
  return toTitle((raw.split(/\s+/)[0] || "there").trim());
};

const resolveTimeZone = (authData) =>
  authData?.timeZone || authData?.hotelTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "Africa/Gaborone";

const hourInTz = (tz) =>
  Number(new Intl.DateTimeFormat("en-GB", { hour: "numeric", hour12: false, timeZone: tz }).format(new Date()));

const pickGreeting = (h) => (h >= 5 && h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening");

const hideLegacyWelcomeSafely = () => {
  if (typeof document === "undefined") return;
  try {
    const el = document.querySelector(".legacy-welcome-banner");
    if (el) el.style.display = "none";
  } catch {}
};

const toDateOrNull = (v) => {
  if (!v) return null;
  if (v instanceof Date) return v;
  const d = new Date(String(v).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};

const ymdInTz = (date, tz) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(date);

const num = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};
const str = (v) => (v == null ? "" : String(v));
const pick = (...vals) => {
  for (const v of vals) if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  return undefined;
};

const normalizePaymentStatus = (row) => {
  const total = num(pick(row.totalAmount, row.TotalAmount, row.amountTotal), 0);
  const paid = num(pick(row.paidAmount, row.PaidAmount, row.amountPaid), 0);
  const raw = pick(row.paymentStatus, row.PaymentStatus);
  if (raw !== undefined && raw !== null && raw !== "") {
    if (typeof raw === "number") return raw >= 2 ? "Paid" : raw === 1 ? "PartiallyPaid" : "Pending";
    const s = String(raw).toLowerCase().replace(/\s|-/g, "");
    if (s.includes("partial")) return "PartiallyPaid";
    if (s === "paid" || s.includes("full")) return "Paid";
    if (s.includes("pending") || s.includes("unpaid")) return "Pending";
  }
  if (total > 0) return paid >= total ? "Paid" : paid > 0 ? "PartiallyPaid" : "Pending";
  return "Unknown";
};

const PAYMENT_CHIP = {
  Paid: { chip: "available", text: "Paid" },
  PartiallyPaid: { chip: "confirmed", text: "Part paid" },
  Pending: { chip: "pending", text: "Pending" },
  Unknown: { chip: "", text: "No charge" },
};

const ROOM_ENUM = { 0: "Available", 1: "Occupied", 2: "Maintenance", 3: "Reserved", 4: "Maintenance", 5: "Cleaning", 6: "Dirty", 7: "OutOfService", 9: "Unknown" };
const roomStatusOf = (s) => {
  if (typeof s === "number") return ROOM_ENUM[s] || "Unknown";
  const v = String(s ?? "").trim();
  if (v !== "" && !Number.isNaN(Number(v))) return ROOM_ENUM[Number(v)] || "Unknown";
  if (v === "UnderMaintenance") return "Maintenance";
  return v || "Unknown";
};

export default function Dashboard() {
  useFlatPage();
  const { authData, logout } = useAuth();
  const navigate = useNavigate();

  const hotelId = authData?.hotelId;
  const hasHotelContext = !!hotelId;
  const isWeekend = useMemo(() => {
    const d = new Date().getDay(); // Sun=0 ... Fri=5 Sat=6
    return d === 5 || d === 6 || d === 0;
  }, []);

  const [dashboardData, setDashboardData] = useState(null);
  const [rooms, setRooms] = useState(null); // null = not loaded (hide room KPIs rather than show zeros)
  const [openMaintenance, setOpenMaintenance] = useState(null);
  const [range, setRange] = useState("7d");
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState(null);
  const [listTab, setListTab] = useState("arrivals");

  const [accent, setAccent] = useState(() => cssVar("--hms-accent", "#b89b6e"));
  useEffect(() => {
    const read = () => setAccent(cssVar("--hms-accent", "#b89b6e"));
    read();
    const obs = new MutationObserver(read);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class", "style", "data-theme"] });
    return () => obs.disconnect();
  }, []);

  const prefersReduced = typeof window !== "undefined" ? window.matchMedia("(prefers-reduced-motion: reduce)").matches : false;

  const firstName = useMemo(() => getFirstName(authData), [authData]);
  const tz = useMemo(() => resolveTimeZone(authData), [authData]);
  const [greet, setGreet] = useState("Welcome");

  useEffect(() => {
    const update = () => setGreet(pickGreeting(hourInTz(tz)));
    update();
    const id = setInterval(update, 60_000);
    return () => clearInterval(id);
  }, [tz]);

  const todayLong = useMemo(
    () => new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric", timeZone: tz }).format(new Date()),
    [tz]
  );
  const todayKey = useMemo(() => ymdInTz(new Date(), tz), [tz]);

  const currencyCode = dashboardData?.currency || "BWP";
  const money = useCallback(
    (n) =>
      `${currencyCode} ${Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    [currencyCode]
  );

  const fetchDashboardData = useCallback(async () => {
    if (!hotelId) {
      setError("Hotel ID is not available. Please log in again.");
      setLoading(false);
      try {
        logout?.();
      } finally {
        navigate("/login", { replace: true });
      }
      return;
    }

    try {
      const res = await axiosInstance.get(`/Dashboard/${hotelId}`, { params: { range } });
      setDashboardData(res.data);
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Failed to load dashboard data.");
      setDashboardData(null);
    } finally {
      setLoading(false);
      setReloading(false);
    }
  }, [hotelId, logout, navigate, range]);

  // Room board + open maintenance: extra context, never blocks the page
  const fetchOperations = useCallback(async () => {
    if (!hotelId) return;
    try {
      const r = await axiosInstance.get(`/RoomStatus/Hotel/${hotelId}`);
      setRooms(Array.isArray(r.data) ? r.data : []);
    } catch {
      setRooms(null);
    }
    try {
      const m = await axiosInstance.get("/Maintenance/GetAllMaintenanceTasks", { params: { hotelId } });
      const list = Array.isArray(m.data) ? m.data : [];
      setOpenMaintenance(list.filter((x) => x.status === "Pending" || x.status === "InProgress").length);
    } catch {
      setOpenMaintenance(null);
    }
  }, [hotelId]);

  useEffect(() => {
    setLoading(true);
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    fetchOperations();
  }, [fetchOperations]);

  const onRefresh = () => {
    setReloading(true);
    fetchDashboardData();
    fetchOperations();
  };

  /* ───────── today: arrivals (from the API) ───────── */
  const arrivals = useMemo(() => {
    const list = Array.isArray(dashboardData?.bookingList) ? dashboardData.bookingList : [];
    return list.map((r, idx) => {
      const totalAmount = num(pick(r.totalAmount, r.TotalAmount, r.amountTotal), 0);
      const paidAmount = num(pick(r.paidAmount, r.PaidAmount, r.amountPaid), 0);
      return {
        key: r.id ?? r.Id ?? r.bookingId ?? r.BookingId ?? idx,
        name: str(pick(r.name, r.Name)) || `${str(pick(r.firstName, r.FirstName))} ${str(pick(r.lastName, r.LastName))}`.trim() || "—",
        roomType: str(pick(r.roomType, r.RoomType, r.roomTypeName, r.RoomTypeName)) || "—",
        checkIn: pick(r.checkIn, r.checkInDate, r.CheckIn, r.CheckInDate),
        checkOut: pick(r.checkOut, r.checkOutDate, r.CheckOut, r.CheckOutDate),
        totalAmount,
        paidAmount,
        dueAmount: Math.max(totalAmount - paidAmount, 0),
        paymentStatus: normalizePaymentStatus({ ...r, totalAmount, paidAmount }),
      };
    });
  }, [dashboardData]);

  /* ───────── today: departures + room board (from the room status list) ───────── */
  const roomBoard = useMemo(() => {
    if (!rooms) return null;
    const counts = { Available: 0, Occupied: 0, Reserved: 0, Dirty: 0, Cleaning: 0, Maintenance: 0, OutOfService: 0, Unknown: 0 };
    rooms.forEach((r) => {
      const s = roomStatusOf(r.status);
      counts[s] = (counts[s] ?? 0) + 1;
    });
    const total = rooms.length;
    const unavailable = counts.OutOfService + counts.Maintenance;
    const sellable = Math.max(total - unavailable, 0);
    return {
      counts,
      total,
      occupancy: sellable > 0 ? Math.round((counts.Occupied / sellable) * 100) : 0,
      toClean: counts.Dirty + counts.Cleaning,
    };
  }, [rooms]);

  const departures = useMemo(() => {
    if (!rooms) return [];
    return rooms
      .filter((r) => {
        const d = toDateOrNull(r.checkOutDate);
        return d && ymdInTz(d, tz) === todayKey && roomStatusOf(r.status) === "Occupied";
      })
      .map((r) => ({ key: r.roomId, roomNumber: r.roomNumber, roomType: r.roomTypeName, floor: r.floor, checkOutDate: r.checkOutDate }));
  }, [rooms, tz, todayKey]);

  /* ───────── payments donut (today's check-ins) ───────── */
  const paymentsToday = useMemo(() => {
    const p = pick(dashboardData?.paymentsToday, dashboardData?.PaymentsToday);
    if (p) {
      return {
        Paid: num(pick(p.paid, p.Paid), 0),
        PartiallyPaid: num(pick(p.partiallyPaid, p.PartiallyPaid), 0),
        Pending: num(pick(p.pending, p.Pending), 0),
        Other: num(pick(p.other, p.Other), 0),
      };
    }
    const agg = { Paid: 0, PartiallyPaid: 0, Pending: 0, Other: 0 };
    arrivals.forEach((r) => {
      if (r.paymentStatus === "Paid") agg.Paid += 1;
      else if (r.paymentStatus === "PartiallyPaid") agg.PartiallyPaid += 1;
      else if (r.paymentStatus === "Pending") agg.Pending += 1;
      else agg.Other += 1;
    });
    return agg;
  }, [dashboardData, arrivals]);

  const paymentsTotal = paymentsToday.Paid + paymentsToday.PartiallyPaid + paymentsToday.Pending + paymentsToday.Other;

  const donut = useMemo(
    () => ({
      data: {
        labels: ["Paid", "Part paid", "Pending", "No charge"],
        datasets: [
          {
            data: [paymentsToday.Paid, paymentsToday.PartiallyPaid, paymentsToday.Pending, paymentsToday.Other],
            backgroundColor: ["#1f8a4c", accent, "#c98a14", "#d9d9e0"],
            borderWidth: 0,
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        cutout: "70%",
        plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${ctx.raw}` } } },
      },
    }),
    [paymentsToday, accent]
  );

  /* ───────── reservations trend: real data only, cut to the selected range ───────── */
  const days = range === "30d" ? 30 : range === "90d" ? 90 : 7;
  const series = useMemo(() => {
    const raw = Array.isArray(dashboardData?.reservationsGraphData) ? dashboardData.reservationsGraphData : [];
    return raw
      .map((d) => ({
        date: d.date ?? d.Date ?? d.label ?? d.day ?? "",
        confirmed: num(d.confirmed ?? d.Confirmed ?? d.bookingsConfirmed ?? 0, 0),
        pending: num(d.pending ?? d.Pending ?? d.bookingsPending ?? 0, 0),
      }))
      .slice(-days);
  }, [dashboardData, days]);

  const seriesHasData = series.some((d) => d.confirmed > 0 || d.pending > 0);

  const lineChart = useMemo(() => {
    const ink = "#6b6b77";
    const rgb = (() => {
      const h = String(accent || "").replace("#", "");
      const f = h.length === 3 ? h.split("").map((x) => x + x).join("") : h;
      if (!/^[0-9a-fA-F]{6}$/.test(f)) return null;
      return `${parseInt(f.slice(0, 2), 16)},${parseInt(f.slice(2, 4), 16)},${parseInt(f.slice(4, 6), 16)}`;
    })();
    const fill = (context) => {
      const ctx = context?.chart?.ctx;
      if (!ctx) return "rgba(0,0,0,0.05)";
      const g = ctx.createLinearGradient(0, 0, 0, context.chart.height || 300);
      g.addColorStop(0, rgb ? `rgba(${rgb},0.26)` : "rgba(0,0,0,0.08)");
      g.addColorStop(1, rgb ? `rgba(${rgb},0)` : "rgba(0,0,0,0)");
      return g;
    };
    return {
      data: {
        labels: series.map((d) => d.date),
        datasets: [
          {
            label: "Confirmed",
            data: series.map((d) => d.confirmed),
            borderColor: accent,
            backgroundColor: fill,
            borderWidth: 2,
            fill: true,
            tension: 0.35,
            pointRadius: 0,
            pointHoverRadius: 4,
          },
          {
            label: "Pending",
            data: series.map((d) => d.pending),
            borderColor: "#9a9aa5",
            borderWidth: 2,
            borderDash: [6, 4],
            fill: false,
            tension: 0.35,
            pointRadius: 0,
            pointHoverRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: prefersReduced ? false : { duration: 400 },
        plugins: {
          legend: { position: "top", align: "end", labels: { color: ink, usePointStyle: true, boxWidth: 8, font: { family: FONT } } },
          tooltip: { mode: "index", intersect: false },
        },
        interaction: { mode: "nearest", axis: "x", intersect: false },
        scales: {
          x: { ticks: { color: ink, maxRotation: 0, autoSkip: true, font: { family: FONT } }, grid: { display: false } },
          y: { beginAtZero: true, ticks: { color: ink, precision: 0, font: { family: FONT } }, grid: { color: "rgba(0,0,0,0.06)" } },
        },
      },
    };
  }, [series, accent, prefersReduced]);

  /* ───────── tables ───────── */
  const arrivalCols = [
    {
      title: "Guest",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (v, r) => (
        <div className="hms-cell-stack">
          <span className="hms-cell-main">{v}</span>
          <span className="hms-cell-sub">{r.roomType}</span>
        </div>
      ),
    },
    { title: "Check-in", dataIndex: "checkIn", key: "checkIn", width: 130, render: (v) => (v ? fmtDate(v) : "—") },
    { title: "Check-out", dataIndex: "checkOut", key: "checkOut", width: 130, render: (v) => (v ? fmtDate(v) : "—") },
    { title: "Paid", dataIndex: "paidAmount", key: "paidAmount", align: "right", width: 150, render: (v) => money(v) },
    {
      title: "Due",
      dataIndex: "dueAmount",
      key: "dueAmount",
      align: "right",
      width: 150,
      render: (v) => <span className={v > 0 ? "hms-cell-main" : "hms-muted"}>{money(v)}</span>,
    },
    {
      title: "Payment",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      width: 130,
      render: (v) => {
        const m = PAYMENT_CHIP[v] || PAYMENT_CHIP.Unknown;
        return <StatusTag status={m.chip}>{m.text}</StatusTag>;
      },
    },
  ];

  const departureCols = [
    { title: "Room", dataIndex: "roomNumber", key: "roomNumber", width: 140, render: (v) => <span className="hms-cell-main">{v}</span> },
    { title: "Type", dataIndex: "roomType", key: "roomType", render: (v) => v || "—" },
    { title: "Floor", dataIndex: "floor", key: "floor", width: 110, render: (v) => v ?? "—" },
    { title: "Check-out", dataIndex: "checkOutDate", key: "checkOutDate", width: 190, render: (v) => fmtDateTime(v) },
  ];

  /* ───────── effects ───────── */
  const hasContent = !!(dashboardData && (arrivals.length || series.length || dashboardData.todayBooking || dashboardData.totalAmount));
  useEffect(() => {
    if (!hasContent) return undefined;
    hideLegacyWelcomeSafely();
    const t1 = setTimeout(hideLegacyWelcomeSafely, 150);
    const t2 = setTimeout(hideLegacyWelcomeSafely, 600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [hasContent]);

  if (loading) {
    return (
      <div className="hms-page dash-page">
        <div className="hms-stack">
          <SectionCard>
            <Skeleton active paragraph={{ rows: 2 }} />
          </SectionCard>
          <SectionCard>
            <Skeleton active paragraph={{ rows: 5 }} />
          </SectionCard>
        </div>
      </div>
    );
  }

  const outstanding = Math.max(num(dashboardData?.totalAmount) - num(dashboardData?.totalRevenue), 0);
  const rangeLabel = range === "30d" ? "30 days" : range === "90d" ? "90 days" : "7 days";

  return (
    <div className="hms-page dash-page">
      <PageHeader
        title={`${greet}, ${firstName}`}
        subtitle={todayLong}
        actions={
          <>
            <Segmented
              value={range}
              onChange={setRange}
              options={[
                { label: "7 days", value: "7d" },
                { label: "30 days", value: "30d" },
                { label: "90 days", value: "90d" },
              ]}
            />
            <Button icon={<IconRefresh />} onClick={onRefresh} loading={reloading}>
              Refresh
            </Button>
          </>
        }
      />

      {error && <Alert style={{ marginBottom: 12 }} type="error" showIcon title="Could not load the dashboard" description={error} />}

      <div className="hms-stack">
        <SectionCard flush title="Today">
          <StatStrip
            items={[
              { label: "Check-ins", value: num(dashboardData?.todayBooking) },
              ...(rooms ? [{ label: "Check-outs", value: departures.length }] : []),
              ...(roomBoard
                ? [
                    { label: "Occupancy", value: roomBoard.occupancy, suffix: `% (${roomBoard.counts.Occupied} of ${roomBoard.total} rooms)` },
                    { label: "Rooms to clean", value: roomBoard.toClean, tone: roomBoard.toClean ? "warn" : undefined },
                  ]
                : []),
              ...(openMaintenance !== null
                ? [{ label: "Open maintenance", value: openMaintenance, tone: openMaintenance ? "warn" : undefined }]
                : []),
            ]}
          />
        </SectionCard>

        <SectionCard flush title={`Last ${rangeLabel}`}>
          <StatStrip
            items={[
              { label: "Billed", value: money(dashboardData?.totalAmount) },
              { label: "Collected", value: money(dashboardData?.totalRevenue), tone: "good" },
              { label: "Outstanding", value: money(outstanding), tone: outstanding > 0 ? "warn" : "good" },
              { label: "Guests", value: num(dashboardData?.totalCustomer) },
            ]}
          />
          <p className="hms-card-hint" style={{ padding: "0 20px 14px" }}>
            Based on bookings with a check-in in this period. Collected is the amount already paid.
          </p>
        </SectionCard>

        <div className="dash-grid">
          <SectionCard flush title={<CardTitle>Reservations</CardTitle>} extra={<span className="hms-cell-sub">Last {rangeLabel}</span>}>
            {seriesHasData ? (
              <div className="dash-chart">
                <LineChart data={lineChart.data} options={lineChart.options} />
              </div>
            ) : (
              <div className="hms-empty" style={{ padding: "60px 20px" }}>
                No reservation activity in this period.
              </div>
            )}
          </SectionCard>

          <SectionCard flush title="Payments today" extra={<span className="hms-cell-sub">Today's check-ins</span>}>
            {paymentsTotal > 0 ? (
              <>
                <div className="dash-donut">
                  <Doughnut data={donut.data} options={donut.options} />
                  <div className="dash-donut-center">
                    <strong>{paymentsTotal}</strong>
                    <span>check-ins</span>
                  </div>
                </div>
                <div className="dash-legend">
                  <StatusTag status="available">Paid {paymentsToday.Paid}</StatusTag>
                  <StatusTag status="confirmed">Part paid {paymentsToday.PartiallyPaid}</StatusTag>
                  <StatusTag status="pending">Pending {paymentsToday.Pending}</StatusTag>
                  <StatusTag status="">No charge {paymentsToday.Other}</StatusTag>
                </div>
              </>
            ) : (
              <div className="hms-empty" style={{ padding: "60px 20px" }}>
                No check-ins today.
              </div>
            )}
          </SectionCard>
        </div>

        <SectionCard flush title="Today's movements">
          <TabBar
            tabs={[
              { key: "arrivals", label: "Arrivals", count: arrivals.length },
              ...(rooms ? [{ key: "departures", label: "Departures", count: departures.length }] : []),
            ]}
            value={listTab}
            onChange={setListTab}
          />
          {listTab === "arrivals" || !rooms ? (
            <Table
              className="hms-table"
              columns={arrivalCols}
              dataSource={arrivals}
              pagination={tablePagination("arrival", { hideOnSinglePage: true })}
              scroll={{ x: 800 }}
              locale={{ emptyText: <div className="hms-empty">No check-ins today</div> }}
            />
          ) : (
            <Table
              className="hms-table"
              columns={departureCols}
              dataSource={departures}
              pagination={tablePagination("departure", { hideOnSinglePage: true })}
              locale={{ emptyText: <div className="hms-empty">No check-outs due today</div> }}
            />
          )}
        </SectionCard>
      </div>

      <TermsGate />

      <PromotionFooterWidget
        visible={hasHotelContext}
        displayName={firstName}
        isWeekend={isWeekend}
        onCreatePromotion={() => {
          navigate("/hotels/promotionspage?create=1&source=dashboard-footer-widget");
        }}
      />
    </div>
  );
}
